///
/// EarthBrowser
///
/// Earthquake Data Provider
///
/// Provides earthquake data from local OGC sync database with custom rendering
///
/// Created by Matt Giger on December 19, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type { DataRow, IDataProvider, DataQuery, QueryResult, ColumnMetadata, DataStats, FilterGroup, ProviderCapabilities } from './types'
import type { EarthquakeProperties } from '@/earthbrowser/dataset-distribution/earthquakes/EarthquakesTable'
import { formatDistanceToNow } from 'date-fns'

export interface EarthquakeQueryOptions {
	startTime?: string
	minMag?: number
}

export class EarthquakeDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'custom' as const
	private queryFn: (options: EarthquakeQueryOptions) => Promise<EarthquakeProperties[]>
	private cachedData: EarthquakeProperties[] = []
	private isInitializedFlag = false

	constructor(config: {
		id: string
		name: string
		queryFn: (options: EarthquakeQueryOptions) => Promise<EarthquakeProperties[]>
	}) {
		this.id = config.id
		this.name = config.name
		this.queryFn = config.queryFn
	}

	async initialize(): Promise<void> {
		if (this.isInitializedFlag) return
		// Fetch initial data
		this.cachedData = await this.queryFn({})
		this.isInitializedFlag = true
	}

	async refreshData(options?: EarthquakeQueryOptions): Promise<void> {
		this.cachedData = await this.queryFn(options || {})
	}

	/**
	 * Update cached data directly (for when data is managed externally)
	 */
	updateCachedData(data: EarthquakeProperties[]): void {
		this.cachedData = data
	}

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true,
		supportsRegex: true,
		supportsGeometry: true,
		supportsInsert: false,
		supportsUpdate: false,
		supportsDelete: false,
		supportsTransactions: false,
		supportsIndexing: false,
		supportsAggregation: false,
		supportsJoins: false,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	private columnMetadata: ColumnMetadata[] = [
		{
			id: 'mag',
			name: 'Magnitude',
			priority: 1,
			minWidth: 104,
			type: 'number',
			nullable: true,
			render: (value: unknown) => {
				const mag = value as number
				if (!mag) return <span className="text-white/40">—</span>

				const colorClass = mag >= 6 ? 'text-red-400' :
									mag >= 5 ? 'text-orange-400' :
									mag >= 4 ? 'text-yellow-400' :
									'text-green-400'

				return (
					<span className={`font-bold ${colorClass}`}>
						{mag.toFixed(1)}
					</span>
				)
			}
		},
		{
			id: 'place',
			name: 'Location',
			priority: 1,
			// Low floor so Time survives in a narrow dock; the fixed layout
			// hands this column the leftover width when there is any
			minWidth: 140,
			type: 'string',
			nullable: true,
			render: (value: unknown) => (
				<div className="font-medium truncate">{value as string}</div>
			)
		},
		{
			id: 'event_time',
			name: 'Time',
			priority: 2,
			minWidth: 132,
			type: 'date',
			nullable: false,
			render: (value: unknown) => {
				const date = new Date(value as string)
				const exactTime = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
				return (
					<span
						// nowrap: in the narrow data dock the table squeezes this
						// column first, and "about 1 hour ago" over three lines
						// triples the row height
						className="text-white/80 cursor-help whitespace-nowrap"
						title={exactTime}
					>
						{formatDistanceToNow(date, { addSuffix: true })}
					</span>
				)
			}
		},
		{
			id: 'depth_km',
			name: 'Depth',
			priority: 3,
			minWidth: 90,
			type: 'number',
			nullable: true,
			render: (value: unknown) => {
				const depth = value as number
				return (
					<span className="text-cyan-400/80">
						{depth ? `${depth.toFixed(1)} km` : '—'}
					</span>
				)
			}
		},
		{
			id: 'alert',
			name: 'Alert',
			priority: 4,
			minWidth: 70,
			type: 'string',
			nullable: true,
			render: (value: unknown) => {
				if (!value) return <span className="text-white/40">—</span>

				const alert = value as string
				const colorClass = alert === 'red' ? 'text-red-400' :
									alert === 'orange' ? 'text-orange-400' :
									alert === 'yellow' ? 'text-yellow-400' :
									'text-green-400'

				return (
					<span className={`font-medium ${colorClass} uppercase text-xs`}>
						{alert}
					</span>
				)
			}
		}
	]
	async getColumns(): Promise<ColumnMetadata[]> {
		return this.columnMetadata
	}

	async getStats(): Promise<DataStats> {
		return {
			totalRows: this.cachedData.length,
			columnCount: this.columnMetadata.length,
			memorySize: JSON.stringify(this.cachedData).length,
			lastModified: new Date()
		}
	}

	async query(query: DataQuery): Promise<QueryResult> {
		let filteredData = [...this.cachedData]

		// Apply full-text search
		if (query.search?.query) {
			const lowerSearch = query.search.query.toLowerCase()
			filteredData = filteredData.filter((row) => {
				return Object.values(row).some((value) => {
					if (value == null) return false
					return String(value).toLowerCase().includes(lowerSearch)
				})
			})
		}

		// Apply sorting
		if (query.sort && query.sort.length > 0) {
			const sortSpec = query.sort[0] // Use first sort spec
			const sortBy = sortSpec.column
			const sortDirection = sortSpec.direction

			filteredData.sort((a, b) => {
				const aVal = a[sortBy as keyof EarthquakeProperties]
				const bVal = b[sortBy as keyof EarthquakeProperties]

				if (aVal == null && bVal == null) return 0
				if (aVal == null) return 1
				if (bVal == null) return -1

				let comparison = 0
				if (typeof aVal === 'string' && typeof bVal === 'string') {
					comparison = aVal.localeCompare(bVal)
				} else if (typeof aVal === 'number' && typeof bVal === 'number') {
					comparison = aVal - bVal
				} else {
					comparison = String(aVal).localeCompare(String(bVal))
				}

				return sortDirection === 'desc' ? -comparison : comparison
			})
		}

		// Calculate pagination
		const totalCount = filteredData.length
		const offset = query.offset || 0
		const limit = query.limit || 100
		const rows = filteredData.slice(offset, offset + limit)

		return {
			rows: rows as unknown as DataRow[],
			totalCount,
			offset,
			limit,
			hasMore: offset + limit < totalCount,
			executionTime: 0,
			fromCache: true
		}
	}

	async count(_filters?: FilterGroup): Promise<number> {
		// Simple count - filtering not implemented yet
		return this.cachedData.length
	}

	async dispose(): Promise<void> {
		this.cachedData = []
		this.isInitializedFlag = false
	}
}
